import { SCHEMAS, VALIDATION_RULES } from "./schemaRegistry";
import { logger, LOG_CATEGORIES } from "../../utils/logger";

/**
 * Validate an entity against its schema
 * @param {Object} entity - Entity to validate
 * @param {string} entityType - Type of entity
 * @param {string} version - Schema version
 * @returns {Promise<boolean>} Validation result
 */
export async function validateEntity(entity, entityType, version) {
    try {
        const schemaKey = version.replace(/\./g, "_");
        const schema = SCHEMAS[entityType]?.[schemaKey];

        if (!schema) {
            throw new Error(`Schema not found for ${entityType} version ${version}`);
        }

        // Validate required fields
        const missingFields = schema.required.filter(field => {
            const value = entity[field];
            return value === undefined || value === null || value === "";
        });

        if (missingFields.length > 0) {
            throw new Error(`Missing required fields for ${entityType}: ${missingFields.join(", ")}`);
        }

        // Validate field types and formats
        const validationErrors = [];
        
        for (const [field, value] of Object.entries(entity)) {
            const fieldError = await validateField(field, value, entityType, version);
            if (fieldError) {
                validationErrors.push(fieldError);
            }
        }

        if (validationErrors.length > 0) {
            throw new Error(`Validation errors for ${entityType}: ${validationErrors.join("; ")}`);
        }

        // Validate business rules
        const businessRuleErrors = await validateBusinessRules(entity, entityType, version);
        if (businessRuleErrors.length > 0) {
            throw new Error(`Business rule violations for ${entityType}: ${businessRuleErrors.join("; ")}`);
        }

        return true;

    } catch (error) {
        await logger.error(LOG_CATEGORIES.SERIALIZATION, "Entity validation failed:", error, {
            entityType,
            version,
            entityId: entity?.id
        });
        throw error;
    }
}

/**
 * Validate a specific field
 * @param {string} field - Field name
 * @param {any} value - Field value
 * @param {string} entityType - Entity type
 * @param {string} version - Schema version
 * @returns {Promise<string|null>} Validation error or null
 */
async function validateField(field, value, entityType, _version) {
    // Skip validation for undefined/null values (handled by required field validation)
    if (value === undefined || value === null) {
        return null;
    }

    // Email validation
    if (field === "email" && VALIDATION_RULES.email.pattern) {
        if (!VALIDATION_RULES.email.pattern.test(value)) {
            return VALIDATION_RULES.email.message;
        }
    }

    // Amount validation
    if (field === "amount" && typeof value === "number") {
        if (value < VALIDATION_RULES.amount.min) {
            return VALIDATION_RULES.amount.message;
        }
    }

    // Percentage validation
    if (field === "expectedReturn" && typeof value === "number") {
        if (value < VALIDATION_RULES.percentage.min || value > VALIDATION_RULES.percentage.max) {
            return VALIDATION_RULES.percentage.message;
        }
    }

    // Date validation
    if (field === "createdAt" || field === "updatedAt" || field === "serializedAt") {
        if (!isValidDate(value)) {
            return VALIDATION_RULES.date.message;
        }
    }

    // ID validation
    if (field === "id" && typeof value !== "string") {
        return "ID must be a string";
    }

    // Status validation for specific entities
    if (field === "status") {
        const validStatuses = getValidStatuses(entityType);
        if (validStatuses && !validStatuses.includes(value)) {
            return `Invalid status. Must be one of: ${validStatuses.join(", ")}`;
        }
    }

    // Role validation
    if (field === "role") {
        const validRoles = ["user", "admin", "agent", "compliance"];
        if (!validRoles.includes(value)) {
            return `Invalid role. Must be one of: ${validRoles.join(", ")}`;
        }
    }

    // ===== NUEVAS VALIDACIONES PARA ENTIDADES ESPECÍFICAS =====

    // Phone validation
    if (field === "telefono" && VALIDATION_RULES.phone.pattern) {
        if (!VALIDATION_RULES.phone.pattern.test(value)) {
            return VALIDATION_RULES.phone.message;
        }
    }

    // DNI validation (Spanish National ID)
    if (field === "dni" && VALIDATION_RULES.dni.pattern) {
        if (!VALIDATION_RULES.dni.pattern.test(value)) {
            return VALIDATION_RULES.dni.message;
        }
    }

    // CIF validation (Spanish Tax ID for companies)
    if (field === "cif" && VALIDATION_RULES.cif.pattern) {
        if (!VALIDATION_RULES.cif.pattern.test(value)) {
            return VALIDATION_RULES.cif.message;
        }
    }

    // Agent-specific validations
    if (entityType === "Agent") {
        if (field === "tipo" && !["interno", "externo"].includes(value)) {
            return "Agent type must be 'interno' or 'externo'";
        }
        if (field === "activo" && typeof value !== "boolean") {
            return "Agent active status must be boolean";
        }
        if (field === "isVerified" && typeof value !== "boolean") {
            return "Agent verification status must be boolean";
        }
        if (field === "isAgency" && typeof value !== "boolean") {
            return "Agency flag must be boolean";
        }
        if (field === "isAgencyOwner" && typeof value !== "boolean") {
            return "Agency owner flag must be boolean";
        }
    }

    // Document-specific validations
    if (entityType === "Document") {
        if (field === "fileSize" && typeof value === "number" && value <= 0) {
            return "File size must be positive";
        }
        if (field === "securityLevel" && !["standard", "high", "critical"].includes(value)) {
            return "Security level must be 'standard', 'high', or 'critical'";
        }
        if (field === "retentionPeriod" && !["1_year", "3_years", "5_years", "10_years", "permanent"].includes(value)) {
            return "Invalid retention period";
        }
    }

    // KYC-specific validations
    if (entityType === "KYC") {
        if (field === "clientType" && !["individual", "corporate"].includes(value)) {
            return "Client type must be 'individual' or 'corporate'";
        }
        if (field === "riskLevel" && !["low", "medium", "high"].includes(value)) {
            return "Risk level must be 'low', 'medium', or 'high'";
        }
        if (field === "politicallyExposed" && typeof value !== "boolean") {
            return "Politically exposed flag must be boolean";
        }
        if (field === "sanctionsCheck" && typeof value !== "boolean") {
            return "Sanctions check flag must be boolean";
        }
        if (field === "fatcaDeclaration" && typeof value !== "boolean") {
            return "FATCA declaration flag must be boolean";
        }
        if (field === "crsDeclaration" && typeof value !== "boolean") {
            return "CRS declaration flag must be boolean";
        }
    }

    // Notification-specific validations
    if (entityType === "Notification") {
        if (field === "read" && typeof value !== "boolean") {
            return "Read status must be boolean";
        }
        if (field === "priority" && !["low", "normal", "high", "urgent"].includes(value)) {
            return "Priority must be 'low', 'normal', 'high', or 'urgent'";
        }
        if (field === "deliveryStatus" && !["pending", "sent", "delivered", "failed"].includes(value)) {
            return "Invalid delivery status";
        }
        if (field === "category" && !["system", "user", "business", "compliance"].includes(value)) {
            return "Invalid notification category";
        }
    }

    // Message-specific validations
    if (entityType === "Message") {
        if (field === "encrypted" && typeof value !== "boolean") {
            return "Encrypted flag must be boolean";
        }
        if (field === "messageType" && !["general", "support", "notification", "alert"].includes(value)) {
            return "Invalid message type";
        }
        if (field === "priority" && !["low", "normal", "high", "urgent"].includes(value)) {
            return "Priority must be 'low', 'normal', 'high', or 'urgent'";
        }
        if (field === "category" && !["communication", "support", "system", "business"].includes(value)) {
            return "Invalid message category";
        }
    }

    return null;
}

/**
 * Validate business rules for an entity
 * @param {Object} entity - Entity to validate
 * @param {string} entityType - Entity type
 * @param {string} _version - Schema version (unused but required for interface consistency)
 * @returns {Promise<Array<string>>} Business rule violations
 */
async function validateBusinessRules(entity, entityType, _version) {
    const violations = [];

    // User business rules
    if (entityType === "User") {
        if (entity.role === "admin" && !entity.isAdmin) {
            violations.push("Admin users must have isAdmin flag set to true");
        }
        if (entity.kycStatus === "completed" && !entity.kycSubmittedAt) {
            violations.push("Completed KYC must have submission timestamp");
        }
    }

    // Opportunity business rules
    if (entityType === "Opportunity") {
        if (entity.amount <= 0) {
            violations.push("Opportunity amount must be positive");
        }
        if (entity.minimumInvestment > entity.amount) {
            violations.push("Minimum investment cannot exceed total amount");
        }
        if (entity.expectedReturn < 0 || entity.expectedReturn > 100) {
            violations.push("Expected return must be between 0 and 100");
        }
    }

    // Investment business rules
    if (entityType === "Investment") {
        if (entity.amount <= 0) {
            violations.push("Investment amount must be positive");
        }
        if (!entity.userId) {
            violations.push("Investment must have a user ID");
        }
        if (!entity.opportunityId) {
            violations.push("Investment must have an opportunity ID");
        }
    }

    // Agent business rules
    if (entityType === "Agent") {
        if (!entity.usuarioId) {
            violations.push("Agent must have a user ID");
        }
        if (entity.isAgency && !entity.agencyName && !entity.agencyId) {
            violations.push("Agency agents must have agency name or ID");
        }
        if (entity.totalCommissions < 0) {
            violations.push("Total commissions cannot be negative");
        }
        if (entity.opportunitiesCreated < 0) {
            violations.push("Opportunities created cannot be negative");
        }
    }

    // Document business rules
    if (entityType === "Document") {
        if (entity.fileSize <= 0) {
            violations.push("Document file size must be positive");
        }
        if (!entity.userId) {
            violations.push("Document must have a user ID");
        }
        if (entity.securityLevel === "critical" && !entity.encryptionKey) {
            violations.push("Critical security documents must have encryption key");
        }
    }

    // KYC business rules
    if (entityType === "KYC") {
        if (!entity.userId) {
            violations.push("KYC must have a user ID");
        }
        if (entity.clientType === "corporate" && !entity.companyName) {
            violations.push("Corporate KYC must have company name");
        }
        if (entity.politicallyExposed && !entity.politicallyExposedDetails) {
            violations.push("Politically exposed persons must have details");
        }
        if (entity.riskLevel === "high" && !entity.enhancedDueDiligence) {
            violations.push("High-risk KYC requires enhanced due diligence");
        }
    }

    // Notification business rules
    if (entityType === "Notification") {
        if (!entity.userId) {
            violations.push("Notification must have a user ID");
        }
        if (!entity.message || entity.message.trim() === "") {
            violations.push("Notification must have a message");
        }
        if (entity.priority === "urgent" && !entity.expiresAt) {
            violations.push("Urgent notifications must have expiration date");
        }
    }

    // Message business rules
    if (entityType === "Message") {
        if (!entity.userId) {
            violations.push("Message must have a user ID");
        }
        if (!entity.text || entity.text.trim() === "") {
            violations.push("Message must have text content");
        }
        if (entity.encrypted && !entity.encryptionKey) {
            violations.push("Encrypted messages must have encryption key");
        }
    }

    return violations;
}

/**
 * Get valid statuses for an entity type
 * @param {string} entityType - Entity type
 * @returns {Array<string>|null} Valid statuses or null if not defined
 */
function getValidStatuses(entityType) {
    const statusMappings = {
        User: ["active", "inactive", "suspended", "pending_kyc"],
        Opportunity: ["pending_approval", "approved", "rejected", "active", "inactive", "completed"],
        Investment: ["pending", "active", "completed", "cancelled", "failed"],
        Transaction: ["pending", "completed", "failed", "cancelled", "refunded"],
        Agent: ["active", "inactive", "suspended", "pending_verification"],
        Document: ["pending", "approved", "rejected", "expired", "archived"],
        KYC: ["pending", "approved", "rejected", "under_review", "requires_additional_info"],
        Notification: ["pending", "sent", "delivered", "failed", "read"],
        Message: ["pending", "sent", "delivered", "read", "failed"]
    };

    return statusMappings[entityType] || null;
}

/**
 * Check if a value is a valid date
 * @param {any} value - Value to check
 * @returns {boolean} True if valid date
 */
function isValidDate(value) {
    if (typeof value === "string") {
        const date = new Date(value);
        return !isNaN(date.getTime());
    }
    if (value instanceof Date) {
        return !isNaN(value.getTime());
    }
    return false;
}

/**
 * Validate schema definition
 * @param {Object} schema - Schema to validate
 * @param {string} entityType - Entity type
 * @returns {boolean} Whether schema is valid
 */
export function validateSchema(schema, entityType) {
    if (!schema.required || !Array.isArray(schema.required)) {
        throw new Error(`Schema for ${entityType} must have required fields array`);
    }

    if (!schema.audit || typeof schema.audit !== "object") {
        throw new Error(`Schema for ${entityType} must have audit configuration`);
    }

    return true;
} 